import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import DocumentTitle from 'react-document-title';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { getPageTitle } from 'hb-react/frontoffice/utils/page';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

const EventContext = createContext();

export default EventContext;

export const useEventContext = () => useContext(EventContext);

export const withEventContext = (Component) => (props) => {
  const contextValue = useEventContext();

  return <Component {...props} {...contextValue} />;
};

const getEventDetailsPageLink = (
  isLoggedIn,
  eventId,
  topicId,
  isWhitelabelGroup,
) => {
  if (topicId && !isWhitelabelGroup) {
    return Routes.topic_event_details_path(topicId, eventId);
  }

  if (!isLoggedIn) {
    return Routes.network_event_path(eventId);
  }

  return Routes.network_events_event_path(eventId);
};

const getEventRegistrationPageLink = (
  isLoggedIn,
  eventId,
  topicId,
  isWhitelabelGroup,
) => {
  if (topicId && !isWhitelabelGroup) {
    return Routes.topic_event_registration_path(topicId, eventId);
  }

  if (!isLoggedIn) {
    return Routes.network_event_registration_path(eventId);
  }

  return Routes.network_events_event_registration_path(eventId);
};

export const EventProvider = ({
  children,
  event,
  config,
  withPageTitle = true,
}) => {
  const { flagAnalyticsChangeTitleTags } = useFeatureFlag();
  const { currentUser } = useDefaultAppContexts();
  const topicContext = useTopicContext();

  const [eventState, setEventState] = useState(event);

  useEffect(() => setEventState(event), [event]);

  const isLoggedIn = currentUser?.id;

  const providerState = useMemo(
    () => ({
      event: eventState,
      updateEvent: setEventState,
      eventDetailsPageLink: getEventDetailsPageLink(
        isLoggedIn,
        event.id,
        topicContext?.topicId,
        !!topicContext?.whitelabelURL,
      ),
      eventRegistrationPageLink: getEventRegistrationPageLink(
        isLoggedIn,
        event.id,
        topicContext?.topicId,
        !!topicContext?.whitelabelURL,
      ),
      config,
    }),
    [eventState, isLoggedIn, topicContext, event.id, config],
  );

  return (
    <EventContext.Provider value={providerState}>
      {withPageTitle && !flagAnalyticsChangeTitleTags && (
        <DocumentTitle
          title={getPageTitle({
            featureName: 'events',
            title: event.title,
          })}
        />
      )}
      {children}
    </EventContext.Provider>
  );
};
